
import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { InfoIcon } from 'lucide-react';

interface UpdateLog {
  id: string;
  report_id: string;
  admin_email: string;
  field_changed: string;
  old_value: string | null;
  new_value: string | null;
  reason: string;
  created_at: string;
}

interface ReportUpdateLogsProps {
  reportId: string;
}

export const ReportUpdateLogs = ({ reportId }: ReportUpdateLogsProps) => {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['report-logs', reportId],
    queryFn: async () => {
      try {
        // Use a type assertion to bypass the TypeScript error
        const { data, error } = await supabase
          .from('report_update_logs' as any)
          .select('*')
          .eq('report_id', reportId)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        // First convert to unknown, then to UpdateLog[] to satisfy TypeScript
        return ((data || []) as unknown) as UpdateLog[];
      } catch (err) {
        console.error('Error fetching report logs:', err);
        return [] as UpdateLog[];
      }
    },
  });

  const formatValue = (value: string | null): string => {
    if (value === null || value === 'null') return '-';
    
    // Handle boolean values displayed as text
    if (value === 'true') return 'ใช่';
    if (value === 'false') return 'ไม่ใช่';
    
    // Handle special status values
    if (value === 'PENDING') return 'รอการตรวจสอบ';
    if (value === 'APPROVED') return 'อนุมัติแล้ว';
    if (value === 'REJECTED') return 'ไม่อนุมัติ';
    
    return value;
  };

  const formatFieldName = (fieldName: string): string => {
    const fieldNameMap: Record<string, string> = {
      'status': 'สถานะรายงาน',
      'learning_status': 'สถานะการเรียนรู้',
      'submitted_on_time': 'ส่งตรงเวลา',
      'class_duration': 'ระยะเวลาเรียน',
      'teaching_duration': 'ระยะเวลาการสอน',
      'rejection_reason': 'เหตุผลที่ไม่อนุมัติ',
    };
    
    return fieldNameMap[fieldName] || fieldName;
  };

  if (isLoading) {
    return <div className="text-center py-4">กำลังโหลดข้อมูล...</div>;
  }

  if (logs.length === 0) {
    return <div className="text-center py-4 text-gray-500">ไม่พบประวัติการแก้ไขรายงาน</div>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>วันที่แก้ไข</TableHead>
            <TableHead>ผู้แก้ไข</TableHead>
            <TableHead>ฟิลด์ที่แก้ไข</TableHead>
            <TableHead>จากค่า</TableHead>
            <TableHead>เป็นค่า</TableHead>
            <TableHead className="text-right">เหตุผล</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <TableRow key={log.id}>
              <TableCell>
                {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss')}
              </TableCell>
              <TableCell>{log.admin_email}</TableCell>
              <TableCell>{formatFieldName(log.field_changed)}</TableCell>
              <TableCell>{formatValue(log.old_value)}</TableCell>
              <TableCell>{formatValue(log.new_value)}</TableCell>
              <TableCell className="text-right">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <InfoIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium">เหตุผลในการแก้ไข:</h4>
                      <p className="text-sm">{log.reason}</p>
                    </div>
                  </PopoverContent>
                </Popover>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
